import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { formatShortDate } from "../chiefComplaints/dateHelpers";
import { interpretationBand, dualUnit } from "./data";
import { TREND_COLOR, TrendDot, TrendTip, useTrendTip } from "../trendChart";
import ModalPortal from "../common/ModalPortal";

/**
 * "View trend" modal for a single anthropometric parameter. Reads the
 * parameter entry's append-only `statusHistory[]` — no per-parameter
 * filtering on the frontend anymore.
 *
 *   • Header    — "<Parameter> Trend" (title case, no inline status chip).
 *   • Chart     — SVG line chart of numericValue over time; line + point
 *                 colour ties to the *current* interpretation (green /
 *                 orange / red). Skipped for `meta.chart === false`
 *                 (Height — audit-only per mock) and `kind: "select"`
 *                 (Blood group).
 *   • Audit     — Date · Value · Reported by · Source, latest first.
 *                 Value stacks metric + imperial. Auto-derived parameters
 *                 (BMI / WHR) collapse to Date + Value only.
 *   • Footer    — "Last updated value: X · <date>" + "Status: <interp chip>".
 */

const W = 680;
const H = 220;
const PAD = { l: 44, r: 20, t: 24, b: 34 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

// Fixed indigo used across every trend chart in the mock. Status colour lives
// on the "Status:" chip in the footer, not on the line — per design spec.

// Board 4090:15075: every trend chart draws in #6a5ef5.
const CHART_COLOR = TREND_COLOR;

const TrendChart = ({ points, paramKey }) => {
  // White hover tooltip in place of the SVG <title> browser tooltip.
  // Wrapper needs `position: relative`, which `.cc-chart` already
  // provides (shared with the CC / Dx trend charts).
  const { wrapRef, tip, showTip, hideTip } = useTrendTip();

  if (!points || points.length === 0) {
    return <div className="cc-chart-empty">No numeric values to chart yet.</div>;
  }

  const minT = points[0]._t;
  const maxT = points[points.length - 1]._t;
  const tRange = maxT - minT || 1;

  const values = points.map((p) => p.numericValue);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = Math.max((hi - lo) * 0.1, 0.5);
  const vLo = lo - pad;
  const vHi = hi + pad;
  const vRange = vHi - vLo || 1;

  const xFor = (t) => PAD.l + ((t - minT) / tRange) * IW;
  const yFor = (v) => PAD.t + IH - ((v - vLo) / vRange) * IH;

  const polylinePoints = points.map((p) => `${xFor(p._t)},${yFor(p.numericValue)}`).join(" ");
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => vLo + f * vRange);

  return (
    <div className="cc-chart an-chart-wrap" ref={wrapRef}>
    <svg className="an-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Trend chart">
      {ticks.map((tk, i) => {
        const y = yFor(tk);
        return (
          <g key={`tick-${i}`}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#eef0f4" />
            <text x={PAD.l - 8} y={y + 3} fontSize="10" fill="#8a94a6" textAnchor="end">
              {tk.toFixed(1)}
            </text>
          </g>
        );
      })}
      <line x1={PAD.l} x2={W - PAD.r} y1={PAD.t + IH} y2={PAD.t + IH} stroke="#d7dae2" />
      <polyline points={polylinePoints} fill="none" stroke={CHART_COLOR} strokeWidth="1.8" />
      {points.map((p, i) => {
        // Tooltip uses the metric unit for the parameter (kg / cm / kg/m² / ratio)
        // — never the per-entry `unit` field, which reflects the user's chosen
        // unit at save time and would mislabel numericValue (always stored in metric).
        const { primary } = dualUnit(paramKey, p.numericValue, "");
        const text = `${formatShortDate(new Date(p._t).toISOString())} · ${primary}`;
        return (
          <TrendDot
            key={`pt-${i}`}
            cx={xFor(p._t)}
            cy={yFor(p.numericValue)}
            scale={0.7}
            onMouseEnter={(ev) => showTip(ev, text)}
            onMouseMove={(ev) => showTip(ev, text)}
            onMouseLeave={hideTip}
          />
        );
      })}
      <text x={PAD.l} y={H - 8} fontSize="10" fill="#8a94a6">
        {formatShortDate(new Date(minT).toISOString())}
      </text>
      <text x={W - PAD.r} y={H - 8} fontSize="10" fill="#8a94a6" textAnchor="end">
        {formatShortDate(new Date(maxT).toISOString())}
      </text>
    </svg>
    <TrendTip tip={tip} />
    </div>
  );
};
TrendChart.propTypes = {
  points: PropTypes.array.isRequired,
  paramKey: PropTypes.string.isRequired,
};

const toTitleCase = (label) =>
  label.replace(/\b([a-z])/g, (_, ch) => ch.toUpperCase());

const TrendModal = ({ meta, paramEntry, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const history = (paramEntry && paramEntry.statusHistory) || [];
  const current = paramEntry && paramEntry.current;

  const sorted = useMemo(() => {
    return history
      .filter((e) => e && e.testDate)
      .map((e) => ({ ...e, _t: new Date(e.testDate).getTime() }))
      .filter((e) => !isNaN(e._t))
      .sort((a, b) => a._t - b._t);
  }, [history]);

  const points = useMemo(
    () => sorted.filter((e) => typeof e.numericValue === "number" && isFinite(e.numericValue)),
    [sorted]
  );

  const currentInterpretation = current ? current.interpretation : "unknown";
  const band = interpretationBand(currentInterpretation);
  // Audit renders newest → oldest by testDate. `sorted` stays ascending
  // because the chart's x-axis needs it in that order; the audit table
  // gets a reversed view so the most-recent reading sits at the top.
  const auditRows = useMemo(() => [...sorted].reverse(), [sorted]);

  const showChart = meta.chart && meta.kind !== "select" && points.length > 0;
  const isAuto = meta.auto;
  // Footer value: numeric parameters use the dual-unit primary/secondary;
  // categorical parameters (Blood group) have numericValue = null and render
  // valueDisplay directly, falling back to "—" when explicitly unset (None).
  const currentDual =
    current && current.numericValue != null
      ? dualUnit(meta.key, current.numericValue, current.unit)
      : { primary: (current && current.valueDisplay) || "—", secondary: "" };

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal cc-modal-trend an-trend-modal"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">{toTitleCase(meta.label)} Trend</span>
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>
  
          <div className="cc-modal-body">
            {showChart && (
              <div className="cc-trend-chart-wrap">
                <TrendChart points={points} paramKey={meta.key} />
              </div>
            )}
  
            <div className="cc-trend-audit">
              <div className="cc-trend-audit-head">
                <span>Audit</span>
                <span className="cc-trend-audit-count">· {sorted.length} entries</span>
              </div>
              <div
                className={`sec-table an-trend-grid${isAuto ? " is-auto" : ""}`}
                role="table"
              >
                <div className="sec-thead" role="row">
                  <span role="columnheader">Date</span>
                  <span role="columnheader">Value</span>
                  {!isAuto && <span role="columnheader">Reported by</span>}
                  {!isAuto && <span role="columnheader">Source</span>}
                </div>
                {auditRows.map((e) => {
                  const dual = dualUnit(meta.key, e.numericValue, e.unit);
                  return (
                    <div className="sec-row" role="row" key={e.historyId}>
                      <span className="sec-cell" role="cell">{formatShortDate(e.testDate)}</span>
                      <span className="sec-cell an-audit-val" role="cell">
                        <span className="an-audit-val-primary">
                          {e.numericValue == null ? e.valueDisplay || "—" : dual.primary}
                        </span>
                        {dual.secondary && (
                          <span className="an-audit-val-secondary">{dual.secondary}</span>
                        )}
                      </span>
                      {!isAuto && (
                        <span className="sec-cell an-reporter" role="cell">
                          {e.reportedByName || "—"}
                        </span>
                      )}
                      {!isAuto && (
                        <span className="sec-cell an-audit-source" role="cell">—</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
  
            {current && (
              <div className="an-footer-summary">
                <p className="an-footer-line">
                  Last updated value:{" "}
                  <strong>
                    {currentDual.primary}
                    {currentDual.secondary ? ` · ${currentDual.secondary}` : ""}
                  </strong>{" "}
                  <span className="an-footer-date">({formatShortDate(current.testDate)})</span>
                </p>
                {currentInterpretation && currentInterpretation !== "unknown" && (
                  <p className="an-footer-line">
                    Status:{" "}
                    <span
                      className="cc-trend-band"
                      style={{ background: band.bg, color: band.fg }}
                    >
                      {band.label}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  meta: PropTypes.object.isRequired,
  paramEntry: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
